/**
 * Application root: providers + the React Router v7 library-mode route table (§12.1, §12.2).
 *
 * ```
 * RootLayout                     global "backend unreachable" banner (§11.2)
 * ├─ /login                      public
 * └─ AuthedLayout                boot gate + single SSE subscription
 *    ├─ /                        Homescreen
 *    ├─ /notifications           Notification history
 *    ├─ /feed                    Global feed (#51)
 *    ├─ /saved                   Saved posts (#66)
 *    ├─ /watch                   Posts to Comment (#71)
 *    ├─ /content                 Content Performance — imported analytics (#87-#93, ?mode=…)
 *    ├─ /view/:t/:id             View screen (?mode=…)
 *    └─ *                        Not found
 * ```
 */

import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createBrowserRouter } from 'react-router';
import { setUnauthorizedHandler } from './api/client';
import { QK } from './api/queryKeys';
import { queryClient } from './app/queryClient';
import { AuthedLayout } from './app/AuthedLayout';
import { NotFoundPage } from './app/NotFoundPage';
import { RootLayout } from './app/RootLayout';
import {
  CONTENT_PATH,
  FEED_PATH,
  HOME_PATH,
  LOGIN_PATH,
  NOTIFICATIONS_PATH,
  SAVED_PATH,
  VIEW_PATH_PATTERN,
  WATCH_PATH,
} from './app/routes';
import { ContentPage } from './features/content/ContentPage';
import { FeedPage } from './features/feed/FeedPage';
import { HomePage } from './features/home/HomePage';
import { LoginPage } from './features/login/LoginPage';
import { NotificationsPage } from './features/notifications/NotificationsPage';
import { SavedPage } from './features/saved/SavedPage';
import { ViewPage } from './features/view/ViewPage';
import { WatchPage } from './features/watch/WatchPage';

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      { path: LOGIN_PATH, element: <LoginPage /> },
      {
        element: <AuthedLayout />,
        children: [
          { path: HOME_PATH, element: <HomePage /> },
          { path: NOTIFICATIONS_PATH, element: <NotificationsPage /> },
          { path: FEED_PATH, element: <FeedPage /> },
          { path: SAVED_PATH, element: <SavedPage /> },
          { path: WATCH_PATH, element: <WatchPage /> },
          { path: CONTENT_PATH, element: <ContentPage /> },
          { path: VIEW_PATH_PATTERN, element: <ViewPage /> },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);

/**
 * A `401` from any endpoint means the session died mid-session: drop the cached gate answer (so
 * the login screen doesn't bounce the user straight back) and route to /login, remembering where
 * they were.
 */
setUnauthorizedHandler(() => {
  queryClient.removeQueries({ queryKey: QK.auth });
  const { pathname, search } = router.state.location;
  if (pathname === LOGIN_PATH) return;
  void router.navigate(LOGIN_PATH, { replace: true, state: { from: pathname + search } });
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}

export default App;
